import React from "react";
import BackgroundCover from "../../components/backgroundCover/BackgroundCover";
import TitleWithDescription from "../../components/TitleWithDescription/TitleWithDescription";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import Title from "../../components/Title/Title";
import { isMobile } from "../../utils/appUtils";
import { useLanguage } from "../../context/LanguageContext";
import { philosophyTranslations } from "../../translations/philosophyTemplate";

const Philosophy: React.FC = () => {
  const { language } = useLanguage();
  const t = philosophyTranslations[language];

  return (
    <div className="min-h-screen bg-white">
      <BackgroundCover
        backgroundHeight={isMobile() ? 400 : undefined}
        imagePosition="center"
        backgroundImage="/images/philosophy_vision/philosophy_background.jpg"
        isWatchPage={true}
      >
        <div className="absolute top-1/2 left-[100px] -translate-y-[200px] z-10 hidden md:block">
          <Title
            firstLine={t.title.firstLine}
            secondLine={<><span className="text-3xl mr-2">{t.title.secondLine}</span></>}
            className=""
            animate={true}
          />
        </div>
      </BackgroundCover>

      <div className="block md:hidden px-4 pt-8">
        <Title
          firstLine={t.title.firstLine}
          secondLine={<><span className="text-3xl mr-2">{t.title.secondLine}</span></>}
          className=""
          animate={true}
        />
      </div>

      <div className="w-full px-4 md:px-0 py-8 md:py-16">
        <div className="flex flex-col items-center">
          <div className="px-4 md:px-24 lg:px-96 text-base text-gray-600 leading-relaxed space-y-4">
            {t.mainContent.intro.map((paragraph: string, index: number) => (
              <p key={index} className={index === 0 ? "text-2xl font-medium" : ""}>
                {paragraph}
              </p>
            ))}
          </div>
        </div>
      </div>

      <div className="relative w-full">
        <div className="relative w-full bg-gradient-to-b from-[#E1D8D3] via-[#FFFFFF] to-[#E1D8D3] py-8 md:py-12">
          <div className="mx-auto px-4 md:px-24 space-y-8 md:space-y-12">
            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: t.mainContent.constantChange.title.firstLine,
                  secondLine: t.mainContent.constantChange.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.constantChange.content.map((paragraph: string, index: number) => (
                      <p key={index} className={index === 1 ? "text-center text-xl font-medium" : ""}>
                        {paragraph}
                      </p>
                    ))}
                    <ul className="list-disc pl-8 space-y-2">
                      {t.mainContent.constantChange.listItems.map((item: string, index: number) => (
                        <li key={index}>{item}</li>
                      ))}
                    </ul>
                    <p className="mt-4">{t.mainContent.constantChange.conclusion}</p>
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub1.jpg",
                  alt: "Time and Change"
                }}
                mediaPosition="right"
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: t.mainContent.mysticWorlds.title.firstLine,
                  secondLine: t.mainContent.mysticWorlds.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.mysticWorlds.content.map((paragraph: string, index: number) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub2.jpg",
                  alt: "Mystic Worlds"
                }}
                mediaPosition="left"
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: t.mainContent.innovativeSpirit.title.firstLine,
                  secondLine: t.mainContent.innovativeSpirit.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.innovativeSpirit.content.map((paragraph: string, index: number) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub3.jpg",
                  alt: "Innovation"
                }}
                mediaPosition="right"
              />
            </div>

            <div className="px-4 md:px-0">
              <TimelineSection
                title={{
                  firstLine: t.mainContent.brandIdentity.title.firstLine,
                  secondLine: t.mainContent.brandIdentity.title.secondLine,
                }}
                description={(
                  <div className="space-y-4">
                    {t.mainContent.brandIdentity.content.map((paragraph: string, index: number) => (
                      <p key={index}>{paragraph}</p>
                    ))}
                  </div>
                )}
                media={{
                  type: 'image',
                  src: "/images/philosophy_vision/philosophy_sub4.jpg",
                  alt: "Brand Identity"
                }}
                mediaPosition="left"
                showLine={false}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Philosophy;
